/**
 * Map of Knowledge Tab
 *
 * Visualizes user's geographic knowledge across all categories.
 * Shows regions mastered, completion rates, and granularity levels.
 */

import { useState, useMemo, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  useKnowledgeByCategory,
  useCombinedKnowledgeMap,
  useCategoryStats,
  useOverallKnowledgeStats,
} from "@/hooks/useKnowledgeStore";
import { useKnowledgeMapGeoJSON } from "@/hooks/useKnowledgeMapGeoJSON";
import { KnowledgeMap } from "@/components/knowledge/KnowledgeMap";
import { RegionDetailsModal } from "@/components/knowledge/RegionDetailsModal";
import type { Category } from "@/db/types";

type CategoryTab = Category | "combined";

/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days ago")
 */
function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

export function MapOfKnowledgeTab() {
  const [activeTab, setActiveTab] = useState<CategoryTab>("combined");

  // Modal state for region details
  const [selectedRegion, setSelectedRegion] = useState<{
    region: string;
    category: Category;
    granularityLevel: number;
    lastQuizSignature?: string;
  } | null>(null);

  // Load knowledge data
  const phoneKnowledge = useKnowledgeByCategory("phone");
  const adminKnowledge = useKnowledgeByCategory("admin");
  const citiesKnowledge = useKnowledgeByCategory("cities");
  const combinedKnowledge = useCombinedKnowledgeMap();

  // Load stats
  const phoneStats = useCategoryStats("phone");
  const adminStats = useCategoryStats("admin");
  const citiesStats = useCategoryStats("cities");
  const overallStats = useOverallKnowledgeStats();

  // Derive current knowledge based on active tab (memoized to prevent unnecessary recalculations)
  const currentKnowledge = useMemo(() => {
    switch (activeTab) {
      case "phone":
        return phoneKnowledge || [];
      case "admin":
        return adminKnowledge || [];
      case "cities":
        return citiesKnowledge || [];
      case "combined":
        return combinedKnowledge || [];
      default:
        return [];
    }
  }, [activeTab, phoneKnowledge, adminKnowledge, citiesKnowledge, combinedKnowledge]);

  // Build GeoJSON for current tab (handled by custom hook)
  const { geoJSON: mapGeoJSON, isLoading: isLoadingMap } = useKnowledgeMapGeoJSON(currentKnowledge, activeTab);

  // Check if initial data is still loading
  const isLoading =
    phoneKnowledge === undefined ||
    adminKnowledge === undefined ||
    citiesKnowledge === undefined ||
    combinedKnowledge === undefined;

  // Handle feature click - open region details modal (memoized to prevent tooltip re-renders)
  const handleFeatureClick = useCallback((_region: string, knowledge: any) => {
    if (knowledge) {
      setSelectedRegion({
        region: knowledge.region,
        category: knowledge.category,
        granularityLevel: knowledge.granularityLevel,
        lastQuizSignature: knowledge.lastQuizSignature,
      });
    }
  }, []);

  return (
    <>
      {/* Category Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as CategoryTab)}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="combined" className="gap-2">
            <span>🗺️</span>
            <span className="hidden sm:inline">Combined</span>
          </TabsTrigger>
          <TabsTrigger value="phone" className="gap-2">
            <span>📞</span>
            <span className="hidden sm:inline">Phone</span>
          </TabsTrigger>
          <TabsTrigger value="admin" className="gap-2">
            <span>🏛️</span>
            <span className="hidden sm:inline">Admin</span>
          </TabsTrigger>
          <TabsTrigger value="cities" className="gap-2">
            <span>🏙️</span>
            <span className="hidden sm:inline">Cities</span>
          </TabsTrigger>
        </TabsList>

        {/* Combined View */}
        <TabsContent value="combined" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title="Regions"
              value={overallStats?.totalRegions || 0}
              subtitle="Countries explored"
              emoji="🌍"
            />
            <StatsCard
              title="Items Learned"
              value={overallStats?.totalItems || 0}
              subtitle="Total places mastered"
              emoji="📍"
            />
            <StatsCard
              title="Last Activity"
              value={
                overallStats?.lastActivityDate
                  ? formatRelativeDate(overallStats.lastActivityDate)
                  : "Never"
              }
              subtitle="Most recent completion"
              emoji="⏱️"
            />
            <StatsCard
              title="Needs Review"
              value={overallStats?.itemsNeedingReview || 0}
              subtitle="Items due for practice"
              emoji="⚠️"
            />
          </div>

          {mapGeoJSON && mapGeoJSON.features.length > 0 ? (
            <KnowledgeMap geoJSON={mapGeoJSON} onFeatureClick={handleFeatureClick} />
          ) : (
            <KnowledgeMapPlaceholder
              knowledge={currentKnowledge}
              isLoading={isLoading || isLoadingMap}
              category="combined"
            />
          )}
        </TabsContent>

        {/* Phone View */}
        <TabsContent value="phone" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title="Regions"
              value={phoneStats?.totalRegions || 0}
              subtitle="Phone area codes"
              emoji="📞"
            />
            <StatsCard
              title="Items Learned"
              value={phoneStats?.totalItems || 0}
              subtitle="Total codes mastered"
              emoji="📍"
            />
            <StatsCard
              title="Last Activity"
              value={
                phoneStats?.lastActivityDate
                  ? formatRelativeDate(phoneStats.lastActivityDate)
                  : "Never"
              }
              subtitle="Most recent completion"
              emoji="⏱️"
            />
            <StatsCard
              title="Needs Review"
              value={phoneStats?.itemsNeedingReview || 0}
              subtitle="Items due for practice"
              emoji="⚠️"
            />
          </div>

          {mapGeoJSON && mapGeoJSON.features.length > 0 ? (
            <KnowledgeMap geoJSON={mapGeoJSON} onFeatureClick={handleFeatureClick} />
          ) : (
            <KnowledgeMapPlaceholder
              knowledge={currentKnowledge}
              isLoading={isLoading || isLoadingMap}
              category="phone"
            />
          )}
        </TabsContent>

        {/* Admin View */}
        <TabsContent value="admin" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title="Regions"
              value={adminStats?.totalRegions || 0}
              subtitle="Admin divisions"
              emoji="🏛️"
            />
            <StatsCard
              title="Items Learned"
              value={adminStats?.totalItems || 0}
              subtitle="Total divisions mastered"
              emoji="📍"
            />
            <StatsCard
              title="Last Activity"
              value={
                adminStats?.lastActivityDate
                  ? formatRelativeDate(adminStats.lastActivityDate)
                  : "Never"
              }
              subtitle="Most recent completion"
              emoji="⏱️"
            />
            <StatsCard
              title="Needs Review"
              value={adminStats?.itemsNeedingReview || 0}
              subtitle="Items due for practice"
              emoji="⚠️"
            />
          </div>

          {mapGeoJSON && mapGeoJSON.features.length > 0 ? (
            <KnowledgeMap geoJSON={mapGeoJSON} onFeatureClick={handleFeatureClick} />
          ) : (
            <KnowledgeMapPlaceholder
              knowledge={currentKnowledge}
              isLoading={isLoading || isLoadingMap}
              category="admin"
            />
          )}
        </TabsContent>

        {/* Cities View */}
        <TabsContent value="cities" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title="Regions"
              value={citiesStats?.totalRegions || 0}
              subtitle="City locations"
              emoji="🏙️"
            />
            <StatsCard
              title="Items Learned"
              value={citiesStats?.totalItems || 0}
              subtitle="Total cities mastered"
              emoji="📍"
            />
            <StatsCard
              title="Last Activity"
              value={
                citiesStats?.lastActivityDate
                  ? formatRelativeDate(citiesStats.lastActivityDate)
                  : "Never"
              }
              subtitle="Most recent completion"
              emoji="⏱️"
            />
            <StatsCard
              title="Needs Review"
              value={citiesStats?.itemsNeedingReview || 0}
              subtitle="Items due for practice"
              emoji="⚠️"
            />
          </div>

          {mapGeoJSON && mapGeoJSON.features.length > 0 ? (
            <KnowledgeMap geoJSON={mapGeoJSON} onFeatureClick={handleFeatureClick} />
          ) : (
            <KnowledgeMapPlaceholder
              knowledge={currentKnowledge}
              isLoading={isLoading || isLoadingMap}
              category="cities"
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Region Details Modal */}
      {selectedRegion && (
        <RegionDetailsModal
          isOpen={!!selectedRegion}
          onClose={() => setSelectedRegion(null)}
          region={selectedRegion.region}
          category={selectedRegion.category}
          granularityLevel={selectedRegion.granularityLevel}
          lastQuizSignature={selectedRegion.lastQuizSignature}
        />
      )}
    </>
  );
}

// Stats card component
interface StatsCardProps {
  title: string;
  value: number | string;
  subtitle: string;
  emoji: string;
}

function StatsCard({ title, value, subtitle, emoji }: StatsCardProps) {
  return (
    <Card className="border border-border/70 bg-white/80 shadow-lg shadow-primary/10 backdrop-blur dark:bg-secondary/70">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className="text-4xl">{emoji}</div>
          <div className="flex-1">
            <div className="text-3xl font-bold">{value}</div>
            <div className="text-sm font-medium text-muted-foreground">
              {title}
            </div>
            <div className="text-xs text-muted-foreground">{subtitle}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Placeholder for the map visualization
interface KnowledgeMapPlaceholderProps {
  knowledge: unknown[];
  isLoading: boolean;
  category: string;
}

function KnowledgeMapPlaceholder({
  knowledge,
  isLoading,
  category,
}: KnowledgeMapPlaceholderProps) {
  if (isLoading) {
    return (
      <Card className="min-h-[500px] flex items-center justify-center rounded-3xl border border-border/70 bg-white/80 shadow-xl shadow-primary/10 backdrop-blur dark:bg-secondary/70">
        <CardContent>
          <p className="text-muted-foreground">Loading knowledge map...</p>
        </CardContent>
      </Card>
    );
  }

  if (knowledge.length === 0) {
    return (
      <Card className="min-h-[500px] flex items-center justify-center border-dashed rounded-3xl border border-border/70 bg-white/80 shadow-xl shadow-primary/10 backdrop-blur dark:bg-secondary/70">
        <CardContent className="text-center space-y-4 p-12">
          <div className="text-6xl">🗺️</div>
          <h3 className="text-2xl font-semibold">No Knowledge Yet</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Complete some quizzes to start building your Map of Knowledge! Your
            mastered regions will appear here.
          </p>
          <div className="pt-4">
            <Badge variant="secondary" className="text-sm">
              {category === "combined"
                ? "All Categories"
                : category.charAt(0).toUpperCase() + category.slice(1)}
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Temporary: Show knowledge data as list until we implement the map
  return (
    <Card className="min-h-[500px] rounded-3xl border border-border/70 bg-white/80 shadow-xl shadow-primary/10 backdrop-blur dark:bg-secondary/70">
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Your Knowledge Regions</h3>
            <Badge variant="secondary">{knowledge.length} regions</Badge>
          </div>

          <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <p>
              <strong>Coming soon:</strong> Interactive world map visualization
              will appear here! For now, you can see your knowledge data below.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
            {knowledge.map((item: any, index) => (
              <div
                key={index}
                className="p-3 border rounded-lg bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="font-medium text-sm">{item.region}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {Math.round(item.completionRate)}% complete
                  {item.isPerfect && (
                    <Badge variant="default" className="ml-2 text-xs">
                      Perfect!
                    </Badge>
                  )}
                </div>
                {item.category && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Category: {item.category}
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-1">
                  Level: {item.granularityLevel}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
